// © Microsoft Corporation. All rights reserved.

import { getUserInfoAsync, getDeviceInfoAsync, getLocationInfoAsync, logErrorAsync } from "./SapphireBridge";
import { UserConfig, UserInformation, DeviceInformation } from "./SapphireTypes";

/** The variable to hold user information. */
let userInformation: UserInformation;

/** The variable to hold device information. */
let deviceInformation: DeviceInformation;

/** The variable to hold location information. */
let locationInformation: any;

/** The variable to hold user configuration. */
let userConfig: UserConfig;

/**
 * Makes the Sapphire API calls to get the user auth information.
 */
export async function initUserConfig(): Promise<void> {
    userConfig = {};

    deviceInformation = await getDeviceInformation();

    if (deviceInformation) {
        userConfig.muid = `m-${deviceInformation.sapphireId}`;
        userConfig.sapphireId = deviceInformation.sapphireId;
        userConfig.anid = deviceInformation.ANID;
        userConfig.adid = deviceInformation.ADID;
        userConfig.market = getValidMarket(deviceInformation.market);
        userConfig.features = deviceInformation.expFeatures;
        userConfig.activityId = deviceInformation.expActivityId;
        userConfig.isDarkMode = deviceInformation.isDarkMode;
        userConfig.platform = deviceInformation.platform;
        userConfig.bridgeVersion = deviceInformation.bridgeVersion;
        setMarketInfo(userConfig);
    }

    await getUserInformationInternal();

    if (userInformation) {
        userConfig.accountType = userInformation.accountType;
        userConfig.isSignedIn = userInformation.isSignedIn;
    }

    await getLocationInformationInternal();

    if (locationInformation) {
        userConfig.latitude = locationInformation.lat;
        userConfig.longitude = locationInformation.lng;
    }

    const urlParams = new URLSearchParams(window.location.search);
    userConfig.isPageRefresh = urlParams && urlParams.get("isPageRefresh") ? urlParams.get("isPageRefresh") === "1" : false;
}

/**
 * Returns user configuration.
 */
export function getUserConfig(): UserConfig {
    return userConfig;
}

/**
 * Returns MSA or AAD access token.
 * @param scope - The scope
 */
export async function getAccessToken(scope: string): Promise<string> {
    if (userInformation.isSignedIn && userInformation.accountType === "AAD") {
        const result = await getUserInfoAsync({
            refresh: true,
            accountType: "AAD"
        });

        try {
            const accessTokenInfo = JSON.parse(result);

            if (accessTokenInfo.accessToken && accessTokenInfo.accessToken.length > 0) {
                return `Bearer ${accessTokenInfo.accessToken}`;
            }
        } catch (exception) {
            logErrorMessage(exception, "SapphireUserConfig.getAccessToken: Failed to parse AAD access token");
        }
    }

    if (userInformation.isSignedIn && userInformation.accountType === "MSA") {
        const result = await getUserInfoAsync({
            refresh: true,
            accountType: "MSA",
            type: "AccessToken",
            scope: scope
        });

        try {
            const accessTokenInfo = JSON.parse(result);

            if (accessTokenInfo.succeed) {
                return accessTokenInfo.accessToken;
            }
        } catch (exception) {
            logErrorMessage(exception, "SapphireUserConfig.getAccessToken: Failed to parse MSA access token");
        }
    }

    return null;
}

/**
 * Makes Sapphire API call to get the device information and return data.
 */
export async function getDeviceInformation(): Promise<DeviceInformation> {
    const info = await getDeviceInfoAsync({});

    try {
        return JSON.parse(info) as DeviceInformation;
    } catch (exception) {
        logErrorMessage(exception, "SapphireUserConfig.getDeviceInformation: Failed to parse response");
    }
}

/**
 * Makes Sapphire API call to get user information.
 */
async function getUserInformationInternal(): Promise<void> {
    const info = await getUserInfoAsync({});

    try {
        userInformation = JSON.parse(info) as UserInformation;
    } catch (exception) {
        logErrorMessage(exception, "SapphireUserConfig.getUserInformationInternal: Failed to parse response");
    }
}

/**
 * Makes Sapphire API call to get location information.
 */
async function getLocationInformationInternal(): Promise<void> {
    const info = await getLocationInfoAsync({});

    try {
        locationInformation = JSON.parse(info);
    } catch (exception) {
        logErrorMessage(exception, "SapphireUserConfig.getLocationInformationInternal: Failed to parse response");
    }
}

/**
 * log error message
 * @param error : error
 * @param customMessage : custom error message
 */
function logErrorMessage(error: Error, customMessage: string): void {
    logErrorAsync({
        type: "Failure",
        name: "HP_WEBVIEW_APP_ERROR",
        error: customMessage,
        data: {
            error: error,
            customMessage: customMessage
        }
    });
}

/**
 * Get valid market
 * @param deviceInforMarket : selected market
 */
function getValidMarket(deviceInforMarket: string): string {
    if (!deviceInforMarket) {
        return "en-us";
    }

    const selectedMarket = deviceInforMarket.toLowerCase();
    // Since OneService does not support en-id , fallback to en-xl
    return selectedMarket === "en-id" ? "en-xl": selectedMarket;
}

/**
 * Set Market Info
 * @param userConfig : UserConfig
 */
function setMarketInfo(userConfig: UserConfig) {
    const data = userConfig.market.split("-");

    if (data && data.length == 2) {
        userConfig.language = data[0];
        userConfig.countryCode = data[1];
    }
}